import { Route, Routes, Navigate } from "react-router-dom";
import { createContext, lazy, Suspense, useContext, useEffect, useMemo, useState } from "react";
import { lookInSession } from "./common/session";

const Navbar = lazy(() => import("./components/navbar.component"));
const UserAuthForm = lazy(() => import("./pages/userAuthForm.page"));
const Editor = lazy(() => import("./pages/editor.pages"));
const HomePage = lazy(() => import("./pages/home.page"));
const BlogPage = lazy(() => import("./pages/blog.page"));
const PageNotFound = lazy(() => import("./pages/404.page"));
const SideNav = lazy(() => import("./components/sidenavbar.component"));
const ChangePassword = lazy(() => import("./pages/change-password.page"));
const EditProfile = lazy(() => import("./pages/edit-profile.page"));
const VerifyOtp = lazy(() => import("./pages/verifyOTP.page"));
const ManageBlogs = lazy(() => import("./pages/manage-blogs.page"));
const SearchPage = lazy(() => import("./pages/search.page"));

export const UserContext = createContext({});

const RequireAdmin = ({ children }) => {
  const { userAuth } = useContext(UserContext);
  const { access_token, is_admin } = userAuth || {};

  if (!access_token) return <Navigate to="/signin" />;
  if (!is_admin) return <Navigate to="/" />;
  return children;
};

const App = () => {
  const [userAuth, setUserAuth] = useState({});

  const applyTheme = () => {
    const savedTheme = localStorage.getItem("theme");
    const isDark =
      savedTheme === "dark" ||
      (savedTheme !== "light" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches);

    document.body.classList.toggle("dark-mode", isDark);
  };

  useEffect(() => {
    let userInSession = lookInSession("user");

    userInSession
      ? setUserAuth(JSON.parse(userInSession))
      : setUserAuth({
          access_token: null,
        });
  }, []);

  useEffect(() => {
    applyTheme();

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleSystemThemeChange = () => {
      if (!localStorage.getItem("theme")) applyTheme();
    };
    const handleStorageThemeSync = (event) => {
      if (event.key === "theme") applyTheme();
    };

    mediaQuery.addEventListener("change", handleSystemThemeChange);
    window.addEventListener("storage", handleStorageThemeSync);

    return () => {
      mediaQuery.removeEventListener("change", handleSystemThemeChange);
      window.removeEventListener("storage", handleStorageThemeSync);
    };
  }, []);

  const userContextValue = useMemo(
    () => ({ userAuth, setUserAuth }),
    [userAuth]
  );

  return (
    <UserContext.Provider value={userContextValue}>
      <Suspense fallback={<div className="h-cover flex items-center justify-center">Loading...</div>}>
        <Routes>
          <Route
            path="/editor"
            element={
              <RequireAdmin>
                <Editor />
              </RequireAdmin>
            }
          />
          <Route
            path="/editor/:blog_id"
            element={
              <RequireAdmin>
                <Editor />
              </RequireAdmin>
            }
          />
          <Route path="/" element={<Navbar />}>
            <Route index element={<HomePage />} />
            <Route
              path="dashboard"
              element={
                <RequireAdmin>
                  <SideNav />
                </RequireAdmin>
              }
            >
              <Route path="blogs" element={<ManageBlogs />} />
            </Route>
            <Route path="settings" element={<SideNav />}>
              <Route path="edit-profile" element={<EditProfile />} />
              <Route path="change-password" element={<ChangePassword />} />
            </Route>
            <Route path="signin" element={<UserAuthForm type="sign-in" />} />
            <Route path="signup" element={<UserAuthForm type="sign-up" />} />
            <Route path="verify-otp" element={<VerifyOtp />} />
            <Route path="blog/:blog_id" element={<BlogPage />} />
            <Route path="search/:query" element={<SearchPage />} />
            <Route path="*" element={<PageNotFound />} />
          </Route>
        </Routes>
      </Suspense>
    </UserContext.Provider>
  );
};

export default App;
